import {
  CollectionGroup,
  IInvoice,
  InvoiceStatus,
  InvoiceType,
  IPractice,
  ITransaction,
  PaidInvoice,
} from '@principle-theorem/principle-core/interfaces';
import { IInvoiceReportRequest } from '@principle-theorem/reporting';
import {
  asyncForAll,
  chunkRange,
  collectionGroupQuery,
  DocumentReference,
  Firestore,
  IDataTable,
  ISODateType,
  ITimePeriod,
  multiFilter,
  query,
  safeChunk,
  toISODate,
  toTimePeriod,
  toTimestamp,
  undeletedQuery,
  where,
  WithRef,
} from '@principle-theorem/shared';
import { sortBy } from 'lodash';
import { from } from 'rxjs';
import { concatMap, scan } from 'rxjs/operators';
import { Invoice } from '../models/invoice/invoice';

interface ITransactionsFromAccountCredit {
  transactionCreatedAt: ISODateType;
  transactionPracticeName: string;
  transactionId: string;
  transactionAmount: number;
}

const headers: Record<keyof ITransactionsFromAccountCredit, string> = {
  transactionCreatedAt: 'Created At',
  transactionPracticeName: 'Practice Name',
  transactionId: 'Transaction ID',
  transactionAmount: 'Transaction Amount',
};

export class CreditNoteTransactionsByPracticeReport {
  async run(
    dateRange: ITimePeriod,
    practiceRef: DocumentReference<IPractice>
  ): Promise<IDataTable<ITransactionsFromAccountCredit>> {
    const practice = await Firestore.getDoc(practiceRef);

    const transactions = await getCreditNoteInvoiceTransactions({
      startDate: toTimestamp(dateRange.from),
      endDate: toTimestamp(dateRange.to),
      practiceRef,
    });

    const results = transactions.map((transaction) => ({
      transactionCreatedAt: toISODate(transaction.createdAt),
      transactionPracticeName: practice.name,
      transactionId: transaction.ref.id,
      transactionAmount: transaction.amount,
    }));

    return this._toDataTables(
      sortBy(results, (row) => row.transactionCreatedAt)
    );
  }

  private _toDataTables(
    data: ITransactionsFromAccountCredit[]
  ): IDataTable<ITransactionsFromAccountCredit> {
    return {
      name: 'Credit Note Transactions Report',
      data,
      columns: Object.entries(headers).map(([key, header]) => ({
        key,
        header,
      })),
    };
  }
}

async function getCreditNoteInvoiceTransactions(
  request: IInvoiceReportRequest
): Promise<WithRef<ITransaction>[]> {
  const totalRange = toTimePeriod(request.startDate, request.endDate);
  return from(chunkRange(totalRange, 6, 'months'))
    .pipe(
      concatMap((range) =>
        from(
          query(
            collectionGroupQuery<PaidInvoice>(CollectionGroup.Invoices),
            where('type', '==', InvoiceType.CreditNote),
            where('practice.ref', '==', request.practiceRef),
            where('createdAt', '>=', toTimestamp(range.from)),
            where('createdAt', '<=', toTimestamp(range.to))
          )
        ).pipe(
          multiFilter((invoice) => !invoice.deleted),
          multiFilter((invoice) => invoice.status === InvoiceStatus.Paid),
          concatMap((invoices) => safeChunk(invoices, 200)),
          concatMap((invoices) => toTransactions(invoices))
        )
      ),
      scan(
        (acc, records) => [...acc, ...records],
        [] as WithRef<ITransaction>[]
      )
    )
    .toPromise();
}

async function toTransactions(
  invoices: WithRef<IInvoice>[]
): Promise<WithRef<ITransaction>[]> {
  const transactions = await asyncForAll(invoices, (invoice) =>
    Firestore.getDocs(undeletedQuery(Invoice.transactionCol(invoice)))
  );

  return transactions.flat();
}
